import React, {useState, useEffect, useMemo, useRef} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'

import {
    useMultiStyleConfig,
    Drawer,
    DrawerOverlay,
    DrawerContent,
    DrawerHeader,
    DrawerBody,
    DrawerCloseButton,
    IconButton,
    VStack,
    HStack,
    Box,
    Text,
    Button,
    Image,
    Badge,
    useDisclosure,
    useBreakpointValue
} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '@salesforce/retail-react-app/app/components/link'
import {
    useProducts,
    useShopperBasketsMutation,
    useShippingMethodsForShipment
} from '@salesforce/commerce-sdk-react'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {useToast} from '@salesforce/retail-react-app/app/hooks/use-toast'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useMiniCartContext} from '../../hooks/use-mini-cart'
import {useSiteCustomPreferences} from '../../hooks/use-site-custom-preferences'
import useSegment from '../../integrations/segment/app/hooks/use-segment.js'
import {useApproachingPromo} from '../../hooks/use-approaching-promo.js'

import CartIcon from '../header/CartIcon'
import {CloseIcon} from '../icons'
import PayPal from '../../pages/checkout/paypal'
import PromoCallout from '../product-tile/promo-callout'
import {getLowStockWarning} from '../product-tile/product-lowstock-warning'
import {getSizeVariantFullName} from '../../utils/custom-utils'
import {TOAST_MESSAGE_REMOVED_ITEM_FROM_CART} from '@salesforce/retail-react-app/app/constants'
import {productUrlBuilder} from '../../utils/url'

const MiniCart = ({basket, totalItems, onMyCartClick, hoverDelay}) => {
    const toast = useToast()
    const {formatMessage} = useIntl()
    const cartListRef = useRef(null)
    const styles = useMultiStyleConfig('Header')
    const minicartStyles = useMultiStyleConfig('Minicart')
    const [autoCloseTimer, setAutoCloseTimer] = useState(null)
    const {isOpen, onOpen, onClose} = useDisclosure()
    const [selectedItem, setSelectedItem] = useState(null)
    const [cartItemLoading, setCartItemLoading] = useState(false)
    const [isPayPalOpen, setIsPayPalOpen] = useState(false)
    const [hoverTimer, setHoverTimer] = useState(null)
    const isDesktop = useBreakpointValue({base: false, lg: true})
    const {locale} = useMultiSite()
    const removeItemFromBasketMutation = useShopperBasketsMutation('removeItemFromBasket')
    const removeGiftCertificateFromBasketMutation = useShopperBasketsMutation(
        'removeGiftCertificateItemFromBasket'
    )
    const updateShippingMethodForShipmentsMutation = useShopperBasketsMutation(
        'updateShippingMethodForShipment'
    )
    const {getcustomPreferencevalue} = useSiteCustomPreferences()
    const segment = useSegment()
    const isPaypalEnabled = getcustomPreferencevalue('pwaPaypalEnabled')
    const currencySymbol = locale?.preferredCurrencySymbol
    const allProductItems = basket?.productItems || []
    const {data: approachingPromo, refetch} = useApproachingPromo(basket?.basketId)

    const scrollToTop = () => {
        if (!cartListRef.current) return

        const list = cartListRef.current
        const prevBehavior = list.style?.scrollBehavior
        list.style.scrollBehavior = 'auto'

        requestAnimationFrame(() => {
            list.scrollTop = 0
            if (prevBehavior !== undefined) {
                list.style.scrollBehavior = prevBehavior
            } else {
                list.style.removeProperty('scroll-behavior')
            }
        })
    }

    const {shouldOpenMiniCart, onMiniCartOpened} = useMiniCartContext()
    const giftCertificateItems = useMemo(
        () =>
            (basket?.giftCertificateItems || []).map((gc) => ({
                ...gc,
                itemId: gc.itemId || gc.giftCertificateItemId
            })),
        [basket]
    )

    const productIds = useMemo(() => {
        const ids = new Set()
        allProductItems.forEach((item) => {
            ids.add(item.productId)
            item?.bundledProductItems?.forEach((sub) => sub.productId && ids.add(sub.productId))
        })
        return Array.from(ids).join(',')
    }, [allProductItems])

    const {data: products} = useProducts(
        {
            parameters: {
                ids: productIds,
                allImages: true,
                perPricebook: true
            }
        },
        {
            enabled: Boolean(productIds),
            select: (result) => {
                return result?.data?.reduce((map, product) => {
                    map[product.id] = product
                    return map
                }, {})
            }
        }
    )

    const cartItems = useMemo(() => {
        const items = []
        let showPayPalButton = true
        allProductItems.map((item) => {
            const product = products?.[item.productId]
            const isPreorderOrBackorder =
                (product?.inventory?.preorderable && product?.inventory?.stockLevel <= 0) ||
                (product?.inventory?.backorderable && product?.inventory?.stockLevel <= 0)

            if (
                isPreorderOrBackorder ||
                product?.c_subscriptionProduct ||
                item?.bundledProductItems?.length > 0
            ) {
                showPayPalButton = false
            }
            const variantName = product?.type?.variant
                ? getSizeVariantFullName(product.variationValues, product.variationAttributes)
                : null
            const bundleItems =
                item?.bundledProductItems?.map((subItem) => {
                    const subProduct = products?.[subItem?.productId]
                    return {
                        id: subItem?.productId,
                        itemId: subItem?.itemId,
                        name: subProduct?.name || subItem.productName,
                        quantity: subItem?.quantity,
                        price: subItem?.price ?? 0,
                        image: subProduct?.imageGroups?.[0]?.images?.[0]?.link
                    }
                }) || null

            const promotion = product?.productPromotions?.[0]
            const hasPromotion =
                promotion?.promotionalPrice && promotion?.promotionalPrice < item?.basePrice

            items.push({
                id: item?.productId,
                itemId: item?.itemId,
                name: product?.name || item?.productId,
                size: variantName,
                quantity: item?.quantity,
                price: item?.basePrice,
                promotionalPrice: hasPromotion ? promotion.promotionalPrice : null,
                isbonusProductLineItem: item?.bonusProductLineItem,
                listprice: product?.tieredPrices[0]?.price ?? item?.price ?? 0,
                saleprice: product?.tieredPrices[1]?.price ?? item?.price ?? 0,
                image: product?.imageGroups?.[0]?.images?.[0]?.link,
                bundleItems,
                showPayPalButton: !showPayPalButton,
                productPromotions: product?.productPromotions
            })
        })
        return {
            items,
            showPayPalButton
        }
    }, [allProductItems, products])

    const isBasketEmpty = (basket) => {
        const isProductItemEmpty = !basket?.productItems || basket?.productItems.length === 0
        const isGCItemEmpty =
            !basket?.giftCertificateItems || basket?.giftCertificateItems.length === 0
        return isProductItemEmpty && isGCItemEmpty
    }

    const handleRemoveProductItem = async (productItem) => {
        setSelectedItem(productItem)
        setCartItemLoading(true)

        toast({
            title: formatMessage(TOAST_MESSAGE_REMOVED_ITEM_FROM_CART, {quantity: 1}),
            status: 'error'
        })

        await removeItemFromBasketMutation.mutateAsync(
            {
                parameters: {
                    basketId: basket?.basketId,
                    itemId: productItem?.itemId
                }
            },
            {
                onSettled: () => {
                    setCartItemLoading(false)
                    setSelectedItem(null)
                },
                onSuccess: (updatedBasket) => {
                    if (isBasketEmpty(updatedBasket)) {
                        onClose()
                    } else {
                        setTimeout(() => {
                            scrollToTop()
                        }, 120)
                    }
                },
                onError: () => {
                    toast({
                        title: formatMessage({defaultMessage: 'Failed to remove item'}),
                        status: 'error'
                    })
                }
            }
        )
    }

    const handleRemoveGiftCertificateItem = async (currentBasket, giftCertificateItemId) => {
        const giftCertificateItem = currentBasket?.giftCertificateItems.find(
            ({giftCertificateItemId: id}) => id === giftCertificateItemId
        )
        if (!giftCertificateItem) return

        setSelectedItem(giftCertificateItem)
        setCartItemLoading(true)

        toast({
            title: formatMessage(TOAST_MESSAGE_REMOVED_ITEM_FROM_CART, {quantity: 1}),
            status: 'error'
        })

        await removeGiftCertificateFromBasketMutation.mutateAsync(
            {
                parameters: {
                    basketId: currentBasket?.basketId,
                    giftCertificateItemId: giftCertificateItem?.giftCertificateItemId
                }
            },
            {
                onSettled: () => {
                    setCartItemLoading(false)
                    setSelectedItem(null)
                },
                onSuccess: (updatedBasket) => {
                    if (isBasketEmpty(updatedBasket)) {
                        onClose()
                    } else {
                        setTimeout(() => {
                            scrollToTop()
                        }, 120)
                    }
                },
                onError: () => {
                    toast({
                        title: formatMessage({defaultMessage: 'Failed to remove item'}),
                        status: 'error'
                    })
                }
            }
        )
    }

    const handleProceedToCart = () => {
        onClose()
        onMyCartClick?.()
    }

    const handleMouseEnter = () => {
        if (isDesktop) {
            const timer = setTimeout(() => {
                onOpen()
            }, hoverDelay)
            setHoverTimer(timer)
            // onOpen()
        }
    }

    const handleMouseLeave = () => {
        if (isDesktop) {
            if (hoverTimer) {
                clearTimeout(hoverTimer)
                setHoverTimer(null)
            }

            // Start auto-close timer when user leaves
            const timer = setTimeout(() => {
                if (!isPayPalOpen) {
                    onClose()
                }
            }, 3000)
            setAutoCloseTimer(timer)
        }
    }

    useEffect(() => {
        return () => {
            if (hoverTimer) {
                clearTimeout(hoverTimer)
            }
            if (autoCloseTimer) {
                clearTimeout(autoCloseTimer)
            }
        }
    }, [hoverTimer, autoCloseTimer])

    useEffect(() => {
        if (shouldOpenMiniCart && isDesktop) {
            onOpen()
            onMiniCartOpened?.()

            // Close minicart after 3 seconds
            setTimeout(() => {
                if (!isPayPalOpen) {
                    onClose()
                }
            }, 3000)
        }
        if (isOpen && isDesktop) {
            document.body.style.overflow = 'hidden'
        } else {
            document.body.style.overflow = 'auto'
        }

        // segment event
        if (segment && segment.sendCartViewed && isDesktop && isOpen && products) {
            segment.sendCartViewed(products, basket)
        }

        // Cleanup, if minicart unmount
        return () => {
            document.body.style.overflow = 'auto'
        }
    }, [
        shouldOpenMiniCart,
        isDesktop,
        onOpen,
        onMiniCartOpened,
        onClose,
        isPayPalOpen,
        isOpen,
        products
    ])
    /*
     * Dependencies:
     * shouldOpenMiniCart - Trigger to auto-open minicart
     * isDesktop - Device type check for desktop-only behavior
     * onOpen - Function to open the minicart drawer
     * onMiniCartOpened - Callback to notify context that minicart opened
     * onClose - Function to close the minicart drawer
     * isPayPalOpen - PayPal modal state to prevent auto-close
     * isOpen - Current minicart open state for body scroll control
     */
    useShippingMethodsForShipment(
        {
            parameters: {
                basketId: basket?.basketId,
                shipmentId: 'me'
            }
        },
        {
            // only fetch if basket is has no shipping method in the first shipment
            enabled:
                !!basket?.basketId &&
                basket?.shipments?.length > 0 &&
                !basket?.shipments[0]?.shippingMethod,
            onSuccess: (data) => {
                updateShippingMethodForShipmentsMutation.mutate({
                    parameters: {
                        basketId: basket?.basketId,
                        shipmentId: 'me'
                    },
                    body: {
                        id: data.defaultShippingMethodId
                    }
                })
            }
        }
    )

    useEffect(() => {
        if (totalItems > 0 && basket?.shipments?.[0]?.shippingMethod?.id) refetch()
    }, [totalItems, basket?.shipments?.[0]?.shippingMethod?.id])

    return (
        <>
            <IconButton
                aria-label={`My cart, number of items: ${totalItems}`}
                icon={
                    <>
                        <CartIcon color={'#ffffff'} _hover={'#ffffff'} />
                        {basket && totalItems > 0 && (
                            <Badge
                                variant="notification"
                                backgroundColor="#d61d46"
                                {...styles.headerIcons}
                                color="#ffffff"
                            >
                                {totalItems}
                            </Badge>
                        )}
                    </>
                }
                variant="unstyled"
                className="headerIcons cartIcon"
                onMouseEnter={basket && totalItems > 0 ? handleMouseEnter : undefined}
                onMouseLeave={handleMouseLeave}
                onClick={onMyCartClick}
                position="relative"
            />

            {isDesktop && (
                <Drawer
                    isOpen={isOpen}
                    onClose={onClose}
                    placement="right"
                    size="md"
                    closeOnOverlayClick
                    closeOnEsc
                >
                    <DrawerOverlay bg="blackAlpha.500" />
                    <DrawerContent
                        width="434px"
                        maxWidth="434px"
                        onMouseEnter={() => {
                            // Clear auto-close timer when user enters minicart content
                            if (autoCloseTimer) {
                                clearTimeout(autoCloseTimer)
                                setAutoCloseTimer(null)
                            }
                        }}
                        onMouseLeave={handleMouseLeave}
                        bg="white"
                    >
                        <DrawerHeader
                            p={'14px 16px 18px 16px !important'}
                            bg="black"
                            color="white"
                            fontSize="xl"
                            fontWeight="extrabold"
                            lineHeight={'7'}
                        >
                            My Cart ({totalItems})
                            <DrawerCloseButton
                                color="white"
                                fontSize="22px"
                                top={'14px'}
                                right={'16px'}
                            />
                        </DrawerHeader>
                        <DrawerBody
                            p="0 !important"
                            display="flex"
                            flexDirection="column"
                            height="calc(100vh - 80px)"
                        >
                            {cartItems.items.length === 0 && giftCertificateItems.length === 0 ? (
                                <VStack spacing={6} align="center" justify="center" flex="1">
                                    <Text
                                        fontSize="1.5rem"
                                        fontWeight="bold"
                                        textAlign="center"
                                        color="#2D3748"
                                    >
                                        <FormattedMessage
                                            defaultMessage="Your shopping cart is empty"
                                            id="minicart.label_empty_cart"
                                        />
                                    </Text>
                                    <Button
                                        variant="outline"
                                        size="lg"
                                        onClick={onClose}
                                        colorScheme="blue"
                                        _hover={{transform: 'translateY(-1px)', boxShadow: 'lg'}}
                                        transition="all 0.2s"
                                    >
                                        <FormattedMessage
                                            defaultMessage="Continue Shopping"
                                            id="minicart.label_continue_shopping"
                                        />
                                    </Button>
                                </VStack>
                            ) : (
                                <>
                                    <VStack
                                        spacing={0}
                                        align="stretch"
                                        flex="1"
                                        overflowY="auto"
                                        ref={cartListRef}
                                    >
                                        {/* Cart Items */}
                                        {cartItems.items.map((item) => (
                                            <Box key={item?.itemId}>
                                                <Box
                                                    p={'5'}
                                                    borderBottom={'1px solid'}
                                                    borderColor={'borderColor1'}
                                                >
                                                    <HStack align="start" spacing={3}>
                                                        <Link
                                                            to={productUrlBuilder({
                                                                productName:
                                                                    item?.productName || item?.name,
                                                                productId:
                                                                    item?.productId || item?.id
                                                            })}
                                                            onClick={() => {
                                                                onClose()
                                                            }}
                                                            boxSize="150px"
                                                            cursor={'pointer'}
                                                        >
                                                            <Image
                                                                src={item?.image}
                                                                alt={item?.name}
                                                                height={'150px'}
                                                                maxW={'100%'}
                                                                borderRadius="md"
                                                                margin={'0 auto'}
                                                            />
                                                        </Link>
                                                        <VStack
                                                            align="start"
                                                            spacing={1}
                                                            flex="1"
                                                            width="calc(100% - 162px)"
                                                        >
                                                            <Link
                                                                to={productUrlBuilder({
                                                                    productName:
                                                                        item?.productName ||
                                                                        item?.name,
                                                                    productId:
                                                                        item?.productId || item?.id
                                                                })}
                                                                onClick={() => {
                                                                    onClose()
                                                                }}
                                                                {...minicartStyles.itemName}
                                                            >
                                                                {item?.name}
                                                            </Link>
                                                            {item?.size && (
                                                                <Text fontSize="sm">
                                                                    Size: {item?.size}
                                                                </Text>
                                                            )}
                                                            <Text {...minicartStyles.itemQty}>
                                                                Quantity : {item?.quantity}
                                                            </Text>
                                                            {getLowStockWarning(
                                                                products?.[item?.id]
                                                            )}
                                                            <PromoCallout
                                                                product={
                                                                    products?.[item?.id] || item
                                                                }
                                                                color="green.500"
                                                            />
                                                        </VStack>
                                                    </HStack>

                                                    {item?.bundleItems &&
                                                        item.bundleItems.length > 0 && (
                                                            <>
                                                                <Box mt={'1'} fontSize={'11px'}>
                                                                    <FormattedMessage
                                                                        defaultMessage="Includes:"
                                                                        id="miniCart.includes"
                                                                    />
                                                                </Box>

                                                                {item.bundleItems.map((subItem) => (
                                                                    <Box key={subItem?.itemId}>
                                                                        <HStack
                                                                            align="start"
                                                                            spacing={3}
                                                                            mt={'2'}
                                                                        >
                                                                            <Box boxSize="150px">
                                                                                <Image
                                                                                    src={
                                                                                        subItem?.image
                                                                                    }
                                                                                    alt={
                                                                                        subItem?.name
                                                                                    }
                                                                                    height={'150px'}
                                                                                    maxW={'100%'}
                                                                                    borderRadius="md"
                                                                                    margin={
                                                                                        '0 auto'
                                                                                    }
                                                                                />
                                                                            </Box>
                                                                            <VStack
                                                                                align="start"
                                                                                spacing={1}
                                                                                width="calc(100% - 162px)"
                                                                            >
                                                                                <Text
                                                                                    {...minicartStyles.itemName}
                                                                                >
                                                                                    {subItem?.name}
                                                                                </Text>
                                                                                <Text
                                                                                    {...minicartStyles.itemQty}
                                                                                >
                                                                                    Quantity:{' '}
                                                                                    {
                                                                                        subItem?.quantity
                                                                                    }
                                                                                </Text>
                                                                                <PromoCallout
                                                                                    product={
                                                                                        products?.[
                                                                                            subItem
                                                                                                ?.id
                                                                                        ] || subItem
                                                                                    }
                                                                                    color="green.500"
                                                                                />
                                                                            </VStack>
                                                                        </HStack>
                                                                    </Box>
                                                                ))}
                                                            </>
                                                        )}
                                                    <Box overflow={'hidden'}>
                                                        <HStack
                                                            justify="space-between"
                                                            width="calc(100% - 162px)"
                                                            float={'right'}
                                                        >
                                                            {item?.promotionalPrice ? (
                                                                <HStack spacing={0}>
                                                                    <Text
                                                                        fontSize="md"
                                                                        fontWeight="black"
                                                                        color="#ef4323"
                                                                        lineHeight={5}
                                                                    >
                                                                        {currencySymbol}
                                                                        {!item?.isbonusProductLineItem
                                                                            ? (
                                                                                  item?.promotionalPrice ??
                                                                                  0 * item?.quantity
                                                                              ).toFixed(2)
                                                                            : (0).toFixed(2)}
                                                                    </Text>
                                                                    <Text
                                                                        fontSize="xs"
                                                                        color="#4a4e58"
                                                                        textDecoration="line-through"
                                                                        ml={'15px'}
                                                                        lineHeight={5}
                                                                        fontWeight="extrabold"
                                                                    >
                                                                        {currencySymbol}
                                                                        {(
                                                                            (item?.listprice ??
                                                                                item?.price) *
                                                                            item?.quantity
                                                                        ).toFixed(2)}
                                                                    </Text>
                                                                </HStack>
                                                            ) : !item?.isbonusProductLineItem ? (
                                                                item?.saleprice &&
                                                                item?.saleprice <
                                                                    item?.listprice ? (
                                                                    <HStack spacing={0}>
                                                                        <Text
                                                                            fontSize="md"
                                                                            fontWeight="black"
                                                                            color="#ef4323"
                                                                            lineHeight={5}
                                                                        >
                                                                            {currencySymbol}
                                                                            {(
                                                                                item?.saleprice ??
                                                                                0 * item?.quantity
                                                                            ).toFixed(2)}
                                                                        </Text>
                                                                        <Text
                                                                            fontSize="xs"
                                                                            color="#4a4e58"
                                                                            textDecoration="line-through"
                                                                            ml={'15px'}
                                                                            lineHeight={5}
                                                                            fontWeight="extrabold"
                                                                        >
                                                                            {currencySymbol}
                                                                            {(
                                                                                item?.listprice ??
                                                                                0 * item?.quantity
                                                                            ).toFixed(2)}
                                                                        </Text>
                                                                    </HStack>
                                                                ) : (
                                                                    <Text
                                                                        fontSize="md"
                                                                        fontWeight="black"
                                                                        lineHeight={5}
                                                                        color="#141519"
                                                                    >
                                                                        {currencySymbol}
                                                                        {(
                                                                            item?.listprice ??
                                                                            0 * item?.quantity
                                                                        ).toFixed(2)}
                                                                    </Text>
                                                                )
                                                            ) : (
                                                                <HStack spacing={0}>
                                                                    <Text
                                                                        fontSize="md"
                                                                        fontWeight="800"
                                                                        color="errorRed1"
                                                                        lineHeight={5}
                                                                    >
                                                                        {currencySymbol}
                                                                        {(0).toFixed(2)}
                                                                    </Text>
                                                                    <Text
                                                                        fontSize="xs"
                                                                        color="charcoalBlue"
                                                                        textDecoration="line-through"
                                                                        ml={'4'}
                                                                        lineHeight={5}
                                                                        fontWeight="extrabold"
                                                                    >
                                                                        {currencySymbol}
                                                                        {(
                                                                            item?.price ??
                                                                            0 * item?.quantity
                                                                        ).toFixed(2)}
                                                                    </Text>
                                                                </HStack>
                                                            )}

                                                            {!item.isbonusProductLineItem && (
                                                                <Button
                                                                    variant="link"
                                                                    color="#a0a0a0"
                                                                    fontSize="xs"
                                                                    textDecoration={'underline'}
                                                                    textUnderlineOffset="3px"
                                                                    onClick={() =>
                                                                        handleRemoveProductItem(
                                                                            item
                                                                        )
                                                                    }
                                                                    isLoading={
                                                                        cartItemLoading &&
                                                                        selectedItem?.itemId ===
                                                                            item?.itemId
                                                                    }
                                                                    leftIcon={
                                                                        <CloseIcon
                                                                            boxSize={3}
                                                                            color="white"
                                                                        />
                                                                    }
                                                                >
                                                                    <FormattedMessage
                                                                        defaultMessage="Remove"
                                                                        id="miniCart.remove"
                                                                    />
                                                                </Button>
                                                            )}
                                                        </HStack>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        ))}

                                        {/* Gift Certificates */}
                                        {giftCertificateItems?.map((gc) => (
                                            <Box key={gc?.itemId}>
                                                <HStack
                                                    align="start"
                                                    spacing={3}
                                                    p={'5'}
                                                    borderBottom={'1px solid'}
                                                    borderColor={'borderColor1'}
                                                >
                                                    <Box boxSize="150px">
                                                        <Image
                                                            src={getAssetUrl(
                                                                'static/img/gift-card.png'
                                                            )}
                                                            alt="Gift Card"
                                                            maxW={'100%'}
                                                            borderRadius="md"
                                                        />
                                                    </Box>
                                                    <VStack align="start" spacing={1}>
                                                        <Text fontWeight="bold">
                                                            <FormattedMessage
                                                                defaultMessage="Crunchyroll Store Digital Gift Card"
                                                                id="miniCart.store_card"
                                                            />
                                                        </Text>
                                                        <Text fontSize="sm">Quantity: 1</Text>
                                                        <Text fontSize="sm">
                                                            To: {gc?.recipientName} <br />
                                                            {gc?.recipientEmail}
                                                        </Text>

                                                        <Text fontSize="sm" mt={2}>
                                                            From: {gc?.senderName} <br />
                                                            {gc?.c_senderEmail}
                                                        </Text>
                                                        <HStack
                                                            justify="space-between"
                                                            width="100%"
                                                        >
                                                            <Text
                                                                fontSize="md"
                                                                fontWeight="black"
                                                                lineHeight={5}
                                                                color="#141519"
                                                            >
                                                                {currencySymbol}
                                                                {Number(gc?.amount).toFixed(2)}
                                                            </Text>
                                                            <Button
                                                                variant="link"
                                                                color="#a0a0a0"
                                                                fontSize="xs"
                                                                textDecoration={'underline'}
                                                                onClick={() =>
                                                                    handleRemoveGiftCertificateItem(
                                                                        basket,
                                                                        gc?.giftCertificateItemId
                                                                    )
                                                                }
                                                                isLoading={
                                                                    cartItemLoading &&
                                                                    selectedItem?.itemId ===
                                                                        gc?.itemId
                                                                }
                                                            >
                                                                <FormattedMessage
                                                                    defaultMessage="Remove"
                                                                    id="miniCart.remove"
                                                                />
                                                            </Button>
                                                        </HStack>
                                                    </VStack>
                                                </HStack>
                                            </Box>
                                        ))}
                                    </VStack>

                                    <Box
                                        p={'16px 16px 15px 16px'}
                                        borderTop="1px solid"
                                        borderColor="borderColor1"
                                        bg="White1"
                                    >
                                        <VStack spacing={2} align="stretch">
                                            <HStack justify="space-between">
                                                <Text
                                                    fontSize="lg"
                                                    fontWeight="extrabold"
                                                    lineHeight={5}
                                                >
                                                    Total
                                                </Text>
                                                <Text
                                                    fontSize="lg"
                                                    fontWeight="extrabold"
                                                    lineHeight={5}
                                                >
                                                    {currencySymbol}
                                                    {(
                                                        Number(basket?.productSubTotal || 0) +
                                                        Number(basket?.c_gcTotal || 0)
                                                    ).toFixed(2)}
                                                </Text>
                                            </HStack>
                                            <Box
                                                color="green.500"
                                                padding="0 0 8px"
                                                fontSize="16px"
                                                fontWeight="800"
                                                lineHeight="20px"
                                                borderRadius="0"
                                                border="none"
                                                fontFamily="Lato, sans-serif"
                                                margin="0"
                                                background="none"
                                            >
                                                {approachingPromo?.approachingShippingDiscounts
                                                    ?.length > 0 && (
                                                    <p>
                                                        {
                                                            approachingPromo
                                                                ?.approachingShippingDiscounts?.[0]
                                                                ?.discountMsg
                                                        }
                                                    </p>
                                                )}
                                            </Box>
                                            <Text mb={2}>
                                                <FormattedMessage
                                                    defaultMessage="Shipping & taxes are calculated at checkout"
                                                    id="minicart.taxes"
                                                />
                                            </Text>
                                            <Button
                                                variant="primary"
                                                size="lg"
                                                width="100%"
                                                _hover={{
                                                    transform: 'translateY(-1px)',
                                                    boxShadow: 'lg'
                                                }}
                                                transition="all 0.2s"
                                                borderRadius={'lg'}
                                                onClick={handleProceedToCart}
                                            >
                                                <FormattedMessage
                                                    defaultMessage="Proceed to Cart"
                                                    id="minicart.proceed_to_cart"
                                                />
                                            </Button>
                                            {isPaypalEnabled && cartItems?.showPayPalButton && (
                                                <PayPal
                                                    onPayPalOpen={() => {
                                                        setIsPayPalOpen(true)
                                                    }}
                                                    onPayPalClose={() => {
                                                        setIsPayPalOpen(false)
                                                    }}
                                                    isExpressPaypal={true}
                                                />
                                            )}
                                        </VStack>
                                    </Box>
                                </>
                            )}
                        </DrawerBody>
                    </DrawerContent>
                </Drawer>
            )}
        </>
    )
}

MiniCart.propTypes = {
    basket: PropTypes.object,
    totalItems: PropTypes.number,
    onMyCartClick: PropTypes.func,
    hoverDelay: PropTypes.number,
    shouldOpenMiniCart: PropTypes.bool,
    onMiniCartOpened: PropTypes.func
}

export default MiniCart
